/**
 * PlotlyFigureView — renders a Plotly figure produced by the data agent
 * (the same `{data, layout, frames}` shape used by the in-app Chart
 * Builder / `PlotlyChartCard`).
 *
 * Unlike `PlotlyChartCard`, this view does NOT execute SQL — it expects
 * fresh `tableData = {columns, rows}` to be passed in (which is the case
 * in chat, where the SSE response already carries the rows). The figure
 * is hydrated against that data via `hydrateFigure`.
 *
 * @param {Object} figure     - Plotly figure spec ({data, layout, frames}).
 * @param {Object} tableData  - {columns: string[], rows: any[][]}.
 * @param {boolean} fill      - When true, fills its container (dashboard cards).
 * @param {Object} layoutOverride - Extra layout props to merge in.
 */
import React, { useMemo } from 'react';
import { Empty, Alert } from 'antd';
import Plot from 'react-plotly.js';
import { rowsToColumnar, hydrateFigure } from '../utils/plotlyData';

/** Shape sniff: looks like a Plotly figure ({data: [trace, ...], layout?}). */
export function isPlotlyFigure(spec) {
  if (!spec || typeof spec !== 'object') return false;
  if (Array.isArray(spec)) return false;
  if (spec.metric) return false;
  if (!Array.isArray(spec.data)) return false;
  return spec.data.some((t) => t && typeof t === 'object' && (t.type || t.xsrc || t.ysrc || t.labelssrc || t.valuessrc));
}

export default function PlotlyFigureView({ figure, tableData, fill = false, layoutOverride }) {
  const parsed = useMemo(() => {
    if (!figure) return null;
    try {
      return typeof figure === 'string' ? JSON.parse(figure) : figure;
    } catch {
      return null;
    }
  }, [figure]);

  const hydrated = useMemo(() => {
    if (!parsed) return null;
    if (tableData?.columns && tableData?.rows) {
      const cols = rowsToColumnar(tableData.columns, tableData.rows);
      return hydrateFigure(parsed, cols);
    }
    // No fresh data: assume the figure already has inline x/y arrays.
    return {
      data: parsed.data || [],
      layout: parsed.layout || {},
      frames: parsed.frames || [],
    };
  }, [parsed, tableData]);

  if (!parsed) {
    return <Alert type="error" showIcon message="Invalid chart spec" />;
  }
  if (!hydrated || !hydrated.data?.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
        <Empty description="No chart data" />
      </div>
    );
  }

  const baseLayout = {
    autosize: true,
    margin: { t: 40, r: 16, b: 48, l: 56 },
    ...(hydrated.layout || {}),
    ...(layoutOverride || {}),
  };

  return (
    <Plot
      data={hydrated.data}
      layout={baseLayout}
      frames={hydrated.frames}
      config={{ displaylogo: false, responsive: true }}
      useResizeHandler
      style={fill ? { width: '100%', height: '100%' } : { width: '100%', height: 400 }}
    />
  );
}
